import type { Editor } from "@tiptap/react";
import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { Asset } from "schemas/generated/asset";
import type {
  ChatAssetItem,
  ChatIntegrationItem,
  ChatIntegrationObjectItem,
  ChatProductItemMinimal,
} from "schemas/generated/chatMessage";
import type { Product } from "schemas/generated/product";
import type { ObjectTypeKey } from "schemas/objectType";

import { serializeChatTokenPayloadToComponent } from "@agent-ui/features/chat/utils/chatTokenSerialization";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";

function createAssetChatItem(asset: Asset): ChatAssetItem {
  return {
    id: asset.id,
    name: asset.name,
    url: asset.url,
    fileExtension: asset.fileExtension,
  };
}

function createProductChatItem(product: Product): ChatProductItemMinimal {
  return {
    id: product.id,
    title: product.title,
    featuredMedia: product.featuredMedia ?? null,
  };
}

function createIntegrationChatItem(
  integrationType: IntegrationType,
): ChatIntegrationItem {
  const config = INTEGRATION_CONFIGS[integrationType];
  return {
    type: integrationType,
    title: config.title,
    image: config.image,
  };
}

function insertContextItemInEditor(
  editor: Editor,
  type: "asset" | "product" | "integration" | "integration-object",
  data:
    | ChatAssetItem
    | ChatProductItemMinimal
    | ChatIntegrationItem
    | ChatIntegrationObjectItem,
  atPosition?: number | null,
) {
  const htmlFragment = serializeChatTokenPayloadToComponent(type, data);
  if (!htmlFragment) {
    return;
  }

  /*
   * NOTE (Kevin 10-02-2025): Trailing space prevents the token from merging
   * with adjacent text when inserted into the editor.
   */
  const html = `${htmlFragment} `;

  if (atPosition !== null && atPosition !== undefined) {
    editor
      .chain()
      .focus()
      .deleteRange({ from: atPosition, to: atPosition + 1 })
      .insertContentAt(atPosition, html)
      .run();
  } else {
    editor.chain().focus().insertContent(html).run();
  }
}

function findAtPositionInEditor(editor: Editor): number | null {
  const { state } = editor;
  const { selection } = state;
  const cursorPos = selection.anchor;

  for (let index = cursorPos - 1; index >= 0; index--) {
    const char = state.doc.textBetween(index, index + 1);
    if (char === "@") {
      return index;
    }
    if (char === " " || index === 0) {
      break;
    }
  }

  return null;
}

function selectContextItem(
  type: "asset" | "product" | "integration" | "integration-object",
  data:
    | ChatAssetItem
    | ChatProductItemMinimal
    | ChatIntegrationItem
    | ChatIntegrationObjectItem,
  editor: Editor | null,
) {
  if (!editor) {
    return;
  }

  const atPosition = findAtPositionInEditor(editor);
  insertContextItemInEditor(editor, type, data, atPosition);
}

export function selectAsset(asset: Asset, editor: Editor | null) {
  const chatAssetItemData = createAssetChatItem(asset);
  selectContextItem("asset", chatAssetItemData, editor);
}

export function selectProduct(product: Product, editor: Editor | null) {
  const productItemData = createProductChatItem(product);
  selectContextItem("product", productItemData, editor);
}

export function selectIntegration(
  integrationType: IntegrationType,
  editor: Editor | null,
) {
  const integrationItemData = createIntegrationChatItem(integrationType);
  selectContextItem("integration", integrationItemData, editor);
}

export function selectIntegrationObject({
  integrationKey,
  objectType,
  object,
  editor,
}: {
  integrationKey: string;
  objectType: ObjectTypeKey;
  object: {
    id: string;
    title?: string;
    handle?: string;
    imageUrl?: string | null;
  };
  editor: Editor | null;
}) {
  const itemData: ChatIntegrationObjectItem = {
    integrationKey,
    objectType,
    object: {
      id: object.id,
      title: object.title,
      handle: object.handle,
      imageUrl: object.imageUrl ?? null,
    },
  };
  selectContextItem("integration-object", itemData, editor);
}
